import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { login } from '../store/slices/authSlice';
import { STREAM_API, setToken, resetSession } from '../utils/streamApi';
import { syncServerCamerasToLocal } from '../utils/localCredStore';
import '../css/login.css';

// Local Mode exists to reach cameras when you're NOT already talking to the
// Parvekshak device directly. If this page itself was already loaded from
// the device's own 192.168.x.x address, that's redundant — hide it there.
const ON_LOCAL_IP = /^192\.168\./.test(window.location.hostname);

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [clientId, setClientId] = useState(
    localStorage.getItem('prvsk_client_id') || process.env.REACT_APP_CLIENT_ID || ''
  );
  const [error,        setError]        = useState('');
  const [loading,      setLoading]      = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (clientId) localStorage.setItem('prvsk_client_id', clientId.trim());
      else          localStorage.removeItem('prvsk_client_id');

      const res = await fetch(`${STREAM_API}/auth/ui/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({
          username,
          password,
          ...(clientId ? { client_id: clientId.trim() } : {}),
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail === 'Account disabled'
          ? 'This account is disabled. Ask an admin or another user to enable it.'
          : 'Invalid username or password');
        return;
      }
      const { token, resetRequired } = await res.json();
      resetSession();
      if (token) {
        setToken(token);
        // Best-effort — populates Local Mode's camera list from the account.
        // Never blocks or fails the login itself (see syncServerCamerasToLocal).
        syncServerCamerasToLocal().catch(() => {});
      }
      dispatch(login({ username, resetRequired }));
      navigate(resetRequired ? '/change-password' : '/', { replace: true });
    } catch {
      setError('Cannot reach server — check connection');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pk-login-root login-page">
      <div className="login-card">
        <div className="login-brand">
          <div className="login-logo">
            <svg width="36" height="36" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="14" cy="14" r="13" stroke="#1f8ef1" strokeWidth="1.5"/>
              <circle cx="14" cy="14" r="7"  stroke="#1f8ef1" strokeWidth="1.5"/>
              <circle cx="14" cy="14" r="2.5" fill="#1f8ef1"/>
              <line x1="14" y1="0"  x2="14" y2="6"  stroke="#1f8ef1" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="14" y1="22" x2="14" y2="28" stroke="#1f8ef1" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="0"  y1="14" x2="6"  y2="14" stroke="#1f8ef1" strokeWidth="1.5" strokeLinecap="round"/>
              <line x1="22" y1="14" x2="28" y2="14" stroke="#1f8ef1" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="login-brand-name">PARVEKSHAK</div>
          <div className="login-brand-sub">AI Surveillance</div>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="login-field">
            <label>Client ID</label>
            <div className="login-input-wrap">
              <span className="login-input-icon">🔌</span>
              <input
                className="login-input"
                type="text"
                placeholder="e.g. home-prvsk"
                value={clientId}
                onChange={e => setClientId(e.target.value)}
                autoComplete="off"
                autoFocus
              />
            </div>
          </div>

          <div className="login-field">
            <label>Username</label>
            <div className="login-input-wrap">
              <span className="login-input-icon">👤</span>
              <input
                className="login-input"
                type="text"
                placeholder="Enter username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                autoComplete="username"
                required
              />
            </div>
          </div>

          <div className="login-field">
            <label>Password</label>
            <div className="login-input-wrap">
              <span className="login-input-icon">🔒</span>
              <input
                className="login-input login-input-pw"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="login-pw-eye"
                onClick={() => setShowPassword(v => !v)}
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div className="login-error">
              <span>⚠️</span> {error}
            </div>
          )}

          <button className="login-btn" type="submit" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <div style={{ marginTop: '24px', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
          <Link
            to="/responder/login"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
              color: '#e63946',
              textDecoration: 'none',
              padding: '8px 16px',
              border: '1px solid rgba(230,57,70,0.3)',
              borderRadius: '6px',
              background: 'rgba(230,57,70,0.05)',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(230,57,70,0.12)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(230,57,70,0.05)'}
          >
            🆘 Login as Responder
          </Link>

          {!ON_LOCAL_IP && (
          <Link
            to="/local-mode"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '13px',
              color: 'var(--text-muted)',
              textDecoration: 'none',
              padding: '8px 16px',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              background: 'transparent',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-alt)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
          >
            🏠 Use Local Mode
          </Link>
          )}
        </div>
      </div>
    </div>
  );
}
